import { URL_REGEX } from "../constant/urls";
import Decimal from "decimal.js";
import { Currency } from "../models/currency";
import { TableCell, TableRow } from "../models/common-table";

export interface ActionButtonConfig {
  text: string;
  cssClass: string;
  disabled: boolean;
}

export function deepCopyValue(value: unknown): unknown {
  if (!Array.isArray(value)) {
    return value;
  }

  return value.map((item: unknown) => {
    if (Array.isArray(item)) {
      return [...item];
    }

    return item;
  });
}

export function cloneRowCells(row: TableRow): TableRow {
  return row.map((cell: TableCell<unknown>) => {
    return {
      ...cell,
      value: deepCopyValue(cell.value),
      selected: Array.isArray(cell.selected) ? [...cell.selected] : cell.selected,
      list: Array.isArray(cell.list) ? [...cell.list] : cell.list,
      originalList: cell.originalList ? [...cell.originalList] : cell.originalList,
    } as TableCell<unknown>;
  });
}

export function getTooltipUrl(tooltip?: string): string | undefined {
  const match: RegExpExecArray | null = URL_REGEX.exec(tooltip || "");
  if (match && match.length > 0 && match[0].length > 0) {
    return match[0];
  }

  return undefined;
}

export function openTooltipUrl(tooltip?: string): void {
  const url: string | undefined = getTooltipUrl(tooltip);
  if (url) {
    window.open(url, "_blank");
  }
}

export function buildTableElementId(
  tableDomId: string,
  section: string,
  rowIndex?: number,
  columnIndex?: number,
  itemIndex?: number,
): string {
  const segments: Array<string | number> = [tableDomId, section];

  if (rowIndex !== undefined) {
    segments.push("r", rowIndex);
  }

  if (columnIndex !== undefined) {
    segments.push("c", columnIndex);
  }

  if (itemIndex !== undefined) {
    segments.push("i", itemIndex);
  }

  return segments.join("-");
}

export function getColumnTotal(
  rows: TableRow[],
  index: number,
  currency: Currency,
  fallbackFormatter: (value: number) => string,
): string {
  let sum = 0;
  for (const row of rows || []) {
    sum += Number(row[index]?.rawValue) || 0;
  }

  return currency ? currency.format.format(sum) : fallbackFormatter(sum);
}

export function getColumnMomTrend(rows: TableRow[], index: number): string {
  let currentSum = new Decimal(0);
  let compareSum = new Decimal(0);

  for (const row of rows) {
    const values = row[index]?.data?.Value as Array<string | number | null> | undefined;
    const currentValue = Number(values?.[0] ?? 0);
    const compareValue = Number(values?.[1] ?? 0);

    if (currentValue !== 0) {
      currentSum = currentSum.add(currentValue);
    }
    if (compareValue !== 0) {
      compareSum = compareSum.add(compareValue);
    }
  }

  const momTrend = compareSum.minus(currentSum).dividedBy(currentSum).mul(100);
  return new Intl.NumberFormat().format(momTrend.toDecimalPlaces(2).toNumber());
}

export function toStateClass(state: string): string {
  if (!state) {
    return "state-unknown";
  }

  const normalizedState: string = state.toLowerCase().replace(/_/g, "-");
  if (normalizedState === "unknown-enum-value") {
    return "state-unknown";
  }

  return `state-${normalizedState}`;
}

export function getActionButtonConfig(state: string): ActionButtonConfig {
  const normalizedState: string = state?.toUpperCase();

  if (normalizedState === "RUNNING") {
    return {
      text: "Stop",
      cssClass: "action-btn btn-stop",
      disabled: false,
    };
  }

  if (normalizedState === "STOPPED") {
    return {
      text: "Start",
      cssClass: "action-btn btn-start",
      disabled: false,
    };
  }

  return {
    text: "Start",
    cssClass: "action-btn btn-disabled",
    disabled: true,
  };
}

export function sanitizeIdentifier(value: string, fallbackValue: string): string {
  const normalizedValue: string = (value || "").toLowerCase().trim();
  if (!normalizedValue) {
    return fallbackValue;
  }

  return normalizedValue.replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "") || fallbackValue;
}

export function getCellValueAsArray(cell: TableCell<unknown> | undefined): unknown[] {
  return Array.isArray(cell?.value) ? cell.value : [];
}

export function getCellValueAsString(cell: TableCell<unknown> | undefined): string {
  return typeof cell?.value === "string" ? cell.value : "";
}


/**
 * Safely converts a value to a string, showing "--" if falsy
 * @param value - The value to convert
 * @returns A string representation of the value, or "--" if value is falsy
 */
export function getCellValueAsStringOrDash(value: unknown): string {
  if (typeof value === "string") return value;
  return value ? String(value) : "--";
}

/**
 * Extracts tooltip from a union type of string, string array, or undefined
 * @param tooltip - The tooltip value to extract
 * @returns The first string value or empty string
 */
export function getTooltipValue(tooltip: string | string[] | undefined): string {
  if (Array.isArray(tooltip)) {
    return tooltip[0] || "";
  }
  return typeof tooltip === "string" ? tooltip : "";
}

/**
 * Extracts icon name from a union type of string, string array, or undefined
 * @param icons - The icon value to extract
 * @returns The first icon name or empty string
 */
export function getIconNameValue(icons: string | string[] | undefined): string {
  if (Array.isArray(icons)) {
    return icons[0] || "";
  }
  return typeof icons === "string" ? icons : "";
}

/**
 * Gets icon name by index from a union type of string, string array, or undefined
 * @param icons - The icon value to extract from
 * @param index - The index to retrieve
 * @returns The icon at the specified index or empty string
 */
export function getIconNameByIndexValue(icons: string | string[] | undefined, index: number): string {
  if (Array.isArray(icons)) {
    return icons[index] || "";
  }
  return typeof icons === "string" ? icons : "";
}

/**
 * Safely checks if an array includes a value
 * @param cellValue - The cell value to check
 * @param selected - The value to check for
 * @returns True if the cell value is an array and includes the selected value
 */
export function cellValueIncludes(cellValue: unknown, selected: unknown): boolean {
  if (Array.isArray(cellValue)) {
    return cellValue.includes(selected);
  }
  return false;
}

/**
 * Gets the length of a cell value as string
 * @param value - The value to get length for
 * @returns The length of the string representation
 */
export function getCellValueLength(value: unknown): number {
  const stringValue: string = getCellValueAsString(value);
  return stringValue ? stringValue.length : 0;
}
